import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import { getLastBlueprintFormattedDate } from '@backrs_ui/grow-ui/src/pages/Survey/survey.util';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectSharedSessionOwnerFirstName } from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';

import { Box, Button, SxProps, Theme, useMediaQuery, Dialog } from '@mui/material';

import { WhatIsACareerTrackModalContent } from '../WhatIsACareerTrackModalContent';

const STRINGS = appStrings.modules.minimumTrack.header;

type HeaderProps = Readonly<{
  userHasBlueprint: boolean;
  userResponseSet: CareerUserResponseSet | null | undefined;
  isShared: boolean;
}>;

export const Header = ({ userHasBlueprint, userResponseSet, isShared = false }: HeaderProps) => {
  const params = useParams();
  const sharedSessionUUID = params.uuid;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 600px)');
  const formattedDate = getLastBlueprintFormattedDate(userResponseSet);
  const sharedOwnerFirstName = useAppSelector(selectSharedSessionOwnerFirstName);

  const handleMyBlueprintClick = () => {
    if (isShared) {
      navigate(APP_ROUTE.SHARED_MY_BLUEPRINT.replace(':uuid', sharedSessionUUID || ''));

      return;
    }

    if (userHasBlueprint) {
      navigate(APP_ROUTE.MY_BLUEPRINT);
    }
  };

  const actionButtons = (
    <Box sx={actionButtonsContainerStyle}>
      <Button
        id="career-track-what-is-button"
        variant="contained"
        size="md"
        hierarchy="Secondary color"
        sx={careerTrackBtnSx}
        onClick={() => setIsModalOpen(true)}
      >
        {STRINGS.careerTrack}
      </Button>

      <Button
        id="career-track-my-blueprint-button"
        variant="contained"
        size="md"
        hierarchy="Primary"
        sx={{ minWidth: 'fit-content' }}
        onClick={handleMyBlueprintClick}
        disabled={!userHasBlueprint}
      >
        {STRINGS.myBlueprint(isShared ? sharedOwnerFirstName : undefined)}
      </Button>
    </Box>
  );

  return (
    <>
      <PageHeader
        title={STRINGS.title}
        subtitle={STRINGS.subtitle(formattedDate)}
        rightComponents={isMobile ? [] : [actionButtons]}
        maxWidth={null}
        sxOverrides={{
          titleContainer: titleContainerSx,
          container: containerSx,
          rightContainer: { flexShrink: 1, minWidth: 0 },
        }}
      />

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: dialogSx,
        }}
      >
        {isModalOpen && <WhatIsACareerTrackModalContent onClose={() => setIsModalOpen(false)} />}
      </Dialog>
    </>
  );
};

const actionButtonsContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  '@container (min-width: 500px)': {
    flexDirection: 'row',
  },
});

const containerSx: SxProps<Theme> = {
  flexWrap: 'wrap',
};

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-xs'],
});

const dialogSx: SxProps<Theme> = ({ backrs }) => ({
  maxWidth: '1098px',
  maxHeight: '90vh',
  borderRadius: backrs.radius['radius-2xl'],
});

const careerTrackBtnSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 'fit-content',
  bgcolor: backrs.colors['Colors/Base/White'],
});
